import { useState, type RefObject } from 'react';
import { SEVERITIES } from '@soc/shared';
import type { MapRef } from 'react-map-gl/maplibre';
import { sevColor, sevGlow } from '../../lib/severity';
import { useGetTowersQuery } from '../../app/api';
import { useActiveAlerts } from '../alerts/selectors';
import { useSelectedProject } from '../projects/useSelectedProject';

const SEV_LABEL = { CRITICAL: 'Critical', HIGH: 'High', MEDIUM: 'Medium', LOW: 'Low' } as const;

export function MapTitle() {
  // Scope to the active project so this shares CellMap's towers cache entry
  // (same query key) rather than firing a second, unscoped request.
  const project = useSelectedProject();
  const { data: towers } = useGetTowersQuery({ polygon: project?.polygon });
  const alerts = useActiveAlerts();
  return (
    <div
      style={{
        position: 'absolute',
        top: 16,
        left: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        pointerEvents: 'none',
      }}
    >
      <span
        className="eyebrow"
        style={{ fontSize: 13, letterSpacing: 2, color: 'var(--text-secondary)' }}
      >
        Tactical Map
      </span>
      <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
        {(towers?.total ?? 0).toLocaleString('en-US')} towers monitored · {alerts.length} active
        anomalies
      </span>
    </div>
  );
}

export function MapLayerToggle() {
  const [active, setActive] = useState('Anomalies');
  const layers = ['Anomalies', 'Coverage'];
  return (
    <div style={{ position: 'absolute', top: 14, right: 16, display: 'flex', gap: 6 }}>
      {layers.map((l) => {
        const isActive = l === active;
        return (
          <button
            key={l}
            onClick={() => setActive(l)}
            style={{
              fontSize: 12,
              fontWeight: isActive ? 600 : 500,
              color: isActive ? 'var(--text-primary)' : 'var(--text-muted)',
              background: isActive ? 'rgba(0,229,255,0.14)' : 'rgba(10,15,22,0.7)',
              border: `1px solid ${isActive ? 'rgba(0,229,255,0.3)' : 'var(--border)'}`,
              borderRadius: 7,
              padding: '6px 11px',
              cursor: 'pointer',
            }}
          >
            {l}
          </button>
        );
      })}
    </div>
  );
}

export function MapLegend() {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        left: 18,
        background: 'rgba(8,12,18,0.82)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '11px 14px',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <span className="eyebrow" style={{ fontSize: 11, letterSpacing: 1.5, color: 'var(--text-muted)' }}>
        Severity
      </span>
      <div style={{ display: 'flex', gap: 16 }}>
        {SEVERITIES.map((sev) => (
          <span
            key={sev}
            style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--text-secondary)' }}
          >
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: sevColor(sev),
                boxShadow: `0 0 7px ${sevGlow(sev)}`,
              }}
            />
            {SEV_LABEL[sev]}
          </span>
        ))}
      </div>
    </div>
  );
}

export function MapZoom({ mapRef }: { mapRef: RefObject<MapRef> }) {
  const btn = {
    width: 30,
    height: 30,
    borderRadius: 7,
    background: 'rgba(10,15,22,0.78)',
    border: '1px solid rgba(120,160,200,0.16)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-secondary)',
    fontSize: 18,
    cursor: 'pointer',
  } as const;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        alignItems: 'center',
      }}
    >
      <button style={btn} onClick={() => mapRef.current?.getMap().zoomIn()}>
        ＋
      </button>
      <button style={btn} onClick={() => mapRef.current?.getMap().zoomOut()}>
        －
      </button>
    </div>
  );
}
